import type { MeqColumnDef, MeqRowData } from "./types";

/**
 * Owned column-sizing helper for MeqTable consumers.
 *
 * Same heuristic as the old wrapper's helper: a column without an explicit `maxSize`
 * gets one derived from its header length (10px per character), so long
 * headers aren't clipped. Deliberately NOT recursive into grouped `columns`
 * — matches the old wrapper's behavior exactly (a group column can also get
 * a maxSize this way, but it's harmless: the engine sizes group header
 * cells from the sum of their leaf columns' sizes, ignoring the group's own
 * maxSize — see plans/ins-1606/learnings.md,
 * refactor-bypass-tables-onto-wrapper entry).
 */

const HEADER_CHAR_WIDTH_PX = 10;

export const calculateMeqColumnMaxSize = <TData extends MeqRowData>(
  column: MeqColumnDef<TData>,
): number | undefined =>
  column.header ? column.header.length * HEADER_CHAR_WIDTH_PX : undefined;

export const withComputedColumnMaxSizes = <TData extends MeqRowData>(
  columns: MeqColumnDef<TData>[],
): MeqColumnDef<TData>[] =>
  columns.map((column) => ({
    ...column,
    maxSize: column.maxSize ?? calculateMeqColumnMaxSize(column),
  }));
